import { Point } from "pixi.js";
import { Container, Graphics } from "@pixi/react";

interface ZoneWarnGraphicProps {
  centroidPoint: Point;
  warnScale?: number;
  setIsHoverWarning?: (h: boolean) => void;
}

interface ConferenceZoneWarningMarkProps {
  warnScale?: number;
  setIsHoverWarning?: (h: boolean) => void;
  eventMode?: "none" | "static";
}

/**
 * Conference zone user-count warning, drawn around local origin (0, 0).
 * Parent positions this at the zone centroid (or offset for row layout).
 */
export function ConferenceZoneWarningMark({
  warnScale = 0.7,
  setIsHoverWarning,
  eventMode = "static"
}: Readonly<ConferenceZoneWarningMarkProps>) {
  const scale = warnScale;

  return (
    <Container data-testid={"warningIcon-grp-cont"}>
      <Graphics
        data-testid={"warningIcon-grp"}
        eventMode={eventMode}
        cursor={eventMode === "static" ? "pointer" : "default"}
        onpointerover={() => setIsHoverWarning?.(true)}
        onpointerout={() => setIsHoverWarning?.(false)}
        draw={g => {
          g.clear();
          g.beginFill(0xe69138);
          g.drawPolygon([
            { x: 0, y: -30 * scale },
            { x: -29 * scale, y: 20 * scale },
            { x: 29 * scale, y: 20 * scale }
          ]);
          g.endFill();
          g.beginFill(0x000000);
          g.drawRect(-3 * scale, -12 * scale, 6 * scale, 19 * scale);
          g.drawRect(-3 * scale, 10 * scale, 6 * scale, 6 * scale);
          g.endFill();
        }}
      />
    </Container>
  );
}

/**
 * warning graphic when user number for conference zone is not matched
 * for now, it's tapping to show warning description.
 */
export function ZoneWarnGraphic({
  centroidPoint,
  warnScale = 0.7,
  setIsHoverWarning
}: Readonly<ZoneWarnGraphicProps>) {
  return (
    <Container position={centroidPoint}>
      <ConferenceZoneWarningMark warnScale={warnScale} setIsHoverWarning={setIsHoverWarning} />
    </Container>
  );
}

export function calculateWarnRatio(
  vwDimensionWidth: number,
  vwDimensionHeight: number,
  backgroundWidth: number,
  backgroundHeight: number
): number {
  const sw = [vwDimensionWidth, backgroundWidth].sort((a, b) => a - b);
  const w = sw[0] / sw[1];
  const sh = [vwDimensionHeight, backgroundHeight].sort((a, b) => a - b);
  const h = sh[0] / sh[1];

  const resWidth = backgroundWidth / vwDimensionWidth;
  const resHeight = backgroundHeight / vwDimensionHeight;

  if (w > h) return resWidth;
  return resHeight;
}
